import { gql, useQuery } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import { Bool, Obj, Str } from '@livecontrol/core-utils';
import { Account } from '@livecontrol/scheduler/model';
import { useEffect, useState } from 'react';
import type { QueryResult } from '../graphql';

interface TVariables {
  clientId: string;
  reauth: boolean;
  callbackPath?: string;
}

export interface TData {
  addFacebookAuthorizations: {
    oauth_url?: string;
  };
}

interface Args {
  account: Account.Like;
  reauth: boolean;
  callbackPath?: string;
}

export const QUERY = gql`
  query AddFacebookAuthorization($clientId: String!, $reauth: Boolean!, $callbackPath: String) {
    addFacebookAuthorizations(clientId: $clientId, reauth: $reauth, callbackPath: $callbackPath) {
      oauth_url
    }
  }
`;

export const useFacebookAuthorization = (args: Args): QueryResult<string> => {
  const [error, setError] = useState<Error | undefined>();
  const [oauthURL, setOAuthURL] = useState<string | undefined>(undefined);

  // Parse the input arguments
  const required = {
    clientId: Str.normalize(Account.toId(args.account)),
    reauth: Bool.normalize(args.reauth)
  };

  // Validate the input
  if (!Obj.isHydrated(required)) {
    return { loading: false, error: Exception.KABOOM };
  }

  /* eslint-disable react-hooks/rules-of-hooks */

  // Prepare the query
  const { loading, data } = useQuery<TData, TVariables>(QUERY, {
    variables: {
      ...required,
      callbackPath: Str.normalize(args.callbackPath)
    },
    onError() {
      setError(Exception.KABOOM);
    }
  });

  // When available, parse server response
  useEffect(() => {
    setOAuthURL(data?.addFacebookAuthorizations.oauth_url);
  }, [data]);

  return { loading, error, data: oauthURL };
};
